import { formatCurrency, formatCurrencyCents } from '../mocks/wipAiFixture'

/**
 * Pure findings engine — same rules as design prototype.
 * @param {{ project: object, wipReport: object, keyDates: array, assignments: array, month: string, today: string }} params
 */
export function computeFindings({ wipReport, keyDates, assignments, month, today }) {
  const findings = []
  const rpt = wipReport.monthlyWipReports.find(r => r.month === month)
  if (!rpt) return findings

  const pacing = parseFloat(String(rpt.wipPacing).replace('%', ''))
  const budget = rpt.budget
  const actual = rpt.totalFeesAndExpenses

  if (pacing > 100) {
    findings.push({
      id: 'f_over',
      severity: 'critical',
      code: 'over_budget',
      anchor: 'kpi',
      title: `Over monthly budget — pacing at ${Math.round(pacing)}%`,
      detail: `Actuals to date ${formatCurrency(
        actual,
      )} exceed the monthly budget of ${formatCurrency(budget)} by ${formatCurrency(
        actual - budget,
      )}. Consider a scope conversation with client before month-end.`,
    })
  }

  const d = new Date(today)
  const dayOfMonth = d.getDate()
  const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  const projection = actual * (daysInMonth / dayOfMonth)
  if (pacing <= 100 && projection > budget) {
    findings.push({
      id: 'f_proj',
      severity: 'warn',
      code: 'projected_over_budget',
      anchor: 'kpi',
      title: `Projected to exceed budget by month-end`,
      detail: `Linear projection: ${formatCurrency(
        projection,
      )} at current burn rate, ${formatCurrency(
        projection - budget,
      )} over budget. Consider easing off non-critical work for the remainder of the month.`,
    })
  }

  if (pacing < 60 && dayOfMonth > 14) {
    findings.push({
      id: 'f_under',
      severity: 'info',
      code: 'under_pacing',
      anchor: 'kpi',
      title: `Under-pacing past mid-month`,
      detail: `At ${Math.round(pacing)}% pacing with ${daysInMonth -
        dayOfMonth} days left. Suggest catch-up on deferred work or re-forecast next month's budget.`,
    })
  }

  const weeksInMonth = 4.33
  const byPerson = {}
  for (const h of rpt.hoursDetails || []) {
    if (!byPerson[h.personName]) byPerson[h.personName] = 0
    byPerson[h.personName] += h.hours
  }
  for (const [name, hours] of Object.entries(byPerson)) {
    const a = assignments.find(x => x.personName === name)
    if (!a) {
      findings.push({
        id: `f_unassigned_${name}`,
        severity: 'warn',
        code: 'unassigned_time',
        anchor: 'utilization',
        title: `${name} logged ${hours}h without an active assignment`,
        detail: `No assignment record found for ${name} on this project. Flag for approval by the project lead.`,
        subjects: [{ kind: 'person', id: name }],
      })
      continue
    }
    const expected = a.weeklyHours * weeksInMonth
    const utilization = (hours / expected) * 100
    if (utilization > 100) {
      findings.push({
        id: `f_over_${name}`,
        severity: 'warn',
        code: 'person_over_allocation',
        anchor: 'utilization',
        title: `${name} is ${Math.round(utilization - 100)}% over assignment`,
        detail: `${name} logged ${hours}h this month vs. ~${Math.round(expected)}h expected (${
          a.weeklyHours
        }h/wk × 4.33 weeks). Consider reallocation or re-assignment review.`,
        subjects: [{ kind: 'person', id: name }],
      })
    }
  }

  const idx = wipReport.monthlyWipReports.findIndex(r => r.month === month)
  if (idx >= 3) {
    const trailing = wipReport.monthlyWipReports
      .slice(idx - 3, idx)
      .map(r => r.submittedExpenses.staffExpenses)
    const mean = trailing.reduce((a, b) => a + b, 0) / trailing.length
    const thisExp = rpt.submittedExpenses.staffExpenses
    if (mean > 0 && thisExp > mean * 1.5) {
      findings.push({
        id: 'f_exp_anom',
        severity: 'warn',
        code: 'expense_anomaly',
        anchor: 'expenses',
        title: `Expenses ${Math.round((thisExp / mean - 1) * 100)}% above 3-month average`,
        detail: `${formatCurrencyCents(thisExp)} this month vs. ${formatCurrencyCents(
          mean,
        )} trailing average. Driven primarily by travel and research credits — worth verifying with the Lead.`,
      })
    }
  }

  if (!keyDates || keyDates.length === 0) {
    findings.push({
      id: 'f_keydates',
      severity: 'info',
      code: 'missing_key_dates',
      anchor: 'keydates',
      title: `No key dates on record`,
      detail: `Add upcoming filings, depositions, or hearings to justify pacing and inform the client narrative.`,
    })
  }

  return findings
}
